import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { addDistributionDataToAttendees } from 'Components/widgets/accommodations/attendees-distribution/AttendeesDistributionUtils';
import { compareAccommodationsByComfort } from 'Utils/proposal/proposalUtils';
import { addInfosToProposalAttendees } from 'Store/booking/attendees/attendees.selector';
import { tripInformationSelector } from 'Store/trip-information/storeHelper';
import {
  getConnectedUserInfoSelector,
  getFilteredAccommodationsAlternativesByComfort,
} from 'Store/booking/booking.selector';
import { Accommodation as AccommodationComponent } from 'Components/widgets/accommodation/Accommodation';
import { useRouter } from 'next/router';
import { getAccommodationProps } from './BookingAccommodationsUtils';
type Props = {
  accommodations: Array<Accommodation>;
  accommodationsCount?: number;
  isInMoreRoomLayer?: boolean;
  isLoading?: boolean;
  proposal: Proposal;
  remainingStock?: number;
  product: Product;
  selectedRate: string;
  setSelectedRate: (arg0: string) => void;
  streetViews?: Array<StreetViewWithAccommodationCategoryId>;
  switchOffers?: (offers: Array<string>) => void;
  theme?: string;
  accommodationsComparator?: Node;
};
export const BookingAccommodations = ({
  accommodations = [],
  accommodationsCount,
  isInMoreRoomLayer = false,
  isLoading = false,
  proposal,
  remainingStock,
  product,
  selectedRate,
  setSelectedRate,
  streetViews,
  switchOffers,
  theme,
  accommodationsComparator,
}: Props) => {
  const tripInformationAttendees = useSelector(tripInformationSelector);
  const connectedUserInfo = useSelector(getConnectedUserInfoSelector);
  const [attendees, setAttendees] = useState({
    adults: [],
    children: [],
  });
  const router = useRouter();
  const isComparator = router.pathname === '/comparator';
  const alternatives = useSelector(getFilteredAccommodationsAlternativesByComfort);
  useEffect(() => {
    const { attendees: formattedAttendees } = addInfosToProposalAttendees({
      proposal,
      tripInformationAttendees,
      connectedUserInfo,
    });
    setAttendees(formattedAttendees);
  }, []);
  const attendeesWithInfos = [...attendees.adults, ...attendees.children];
  const sortedAccommodations = [...accommodations].sort(compareAccommodationsByComfort);
  const arrangementAttendees = sortedAccommodations.reduce((attendeesList, accommodation) => {
    if (accommodation.attendeesDistribution && accommodation.attendeesDistribution.attendees) {
      return [...attendeesList, ...accommodation.attendeesDistribution.attendees];
    }

    return [...attendeesList];
  }, []);

  /**
   * On accommodations view, when accommodations have arrangement, we would like to have index related
   * to accommodations order (first accommodation attendees with first indexes)
   */
  const sortedAttendeesWithInfos =
    !isInMoreRoomLayer && arrangementAttendees && arrangementAttendees.length
      ? arrangementAttendees.map((attendee) => {
          const attendeeData = attendeesWithInfos.find(({ id }) => attendee.id === id) || {};
          return { ...attendee, ...attendeeData };
        })
      : attendeesWithInfos;
  const attendeesWithDistributionData = addDistributionDataToAttendees(
    sortedAttendeesWithInfos,
    proposal.arrivalDate,
  );
  return (
    <AccommodationComponent
      {...getAccommodationProps({
        accommodations: accommodations,
        accommodationsCount,
        alternatives,
        attendeesWithDistributionData,
        isComparator,
        isInMoreRoomLayer,
        proposal,
        remainingStock,
        product,
        streetViews,
      })}
      accommodationsComparator={accommodationsComparator}
      isInMoreRoomLayer={isInMoreRoomLayer}
      isLoading={isLoading}
      selectedRate={selectedRate}
      setSelectedRate={setSelectedRate}
      switchOffers={switchOffers}
      theme={theme}
    />
  );
};
